import * as Sentry from '@sentry/react';

import { getConfig } from '../../config';

import type { ExtrasAndTags } from './types';

const DEFAULT_SENTRY_DSN =
	'https://8b156bc879d5d53284bbaab24c837162@o55978.ingest.us.sentry.io/4511232360710144';
const DEFAULT_PACKAGE_NAME = 'twg-microsite';
const DEFAULT_TEAM_NAME = 'twg-graphyte';
const DEFAULT_PRODUCT = 'twgMicrosite';
const DEFAULT_LOGGER = 'logger.default';

const getEnvironment = (): string => {
	const { env } = getConfig();
	switch (env) {
		case 'production':
			return 'production';
		case 'staging':
			return 'staging';
		case 'development':
		case 'local':
		default:
			return 'dev';
	}
};

/**
 * Initialises the global Sentry client for twg-microsite.
 *
 * - Disabled in dev environments (localhost / development / local) to avoid noise.
 * - Uses `beforeSend` to only capture events that were explicitly tagged via
 *   `logException` / `logMessage`, preventing unwanted global errors from being sent.
 * - Sends default PII (e.g. IP addresses) as requested by Sentry project config.
 */
export const initSentry = (): void => {
	const environment = getEnvironment();

	Sentry.init({
		dsn: DEFAULT_SENTRY_DSN,
		environment,
		enabled: environment !== 'dev',
		integrations: [Sentry.browserTracingIntegration()],
		// Capture 100% of transactions for performance monitoring.
		// Revisit and lower in production once traffic baselines are known.
		tracesSampleRate: 1.0,
		beforeSend: (event) => {
			// Only filter *error* events — let non-error events (e.g. transactions
			// emitted by browserTracingIntegration) through so performance
			// monitoring data still reaches Sentry. Sentry sets `event.type` to
			// values like 'transaction' / 'replay_event' for non-error events;
			// it is `undefined` for error events.
			if (event.type === undefined && event.tags?.name === undefined) {
				// Only capture error events that were explicitly tagged via
				// logException / logMessage, to avoid unwanted global errors.
				return null;
			}
			return event;
		},
		initialScope: {
			tags: {
				packageName: DEFAULT_PACKAGE_NAME,
				teamName: DEFAULT_TEAM_NAME,
				product: DEFAULT_PRODUCT,
				logger: DEFAULT_LOGGER,
			},
		},
	});
};

const setTagsFromExtras = (scope: Sentry.Scope, extrasAndTags: ExtrasAndTags): void => {
	scope.setTag('name', DEFAULT_PRODUCT);

	if (extrasAndTags.loggerName !== undefined && extrasAndTags.loggerName.length !== 0) {
		scope.setTag('logger', `logger.${extrasAndTags.loggerName}`);
	}
	if (extrasAndTags.accountId !== undefined && extrasAndTags.accountId.length !== 0) {
		scope.setUser({ id: extrasAndTags.accountId });
	}
	if (extrasAndTags.teamName !== undefined && extrasAndTags.teamName.length !== 0) {
		scope.setTag('teamName', extrasAndTags.teamName);
	}
	if (extrasAndTags.touchpointId !== undefined && extrasAndTags.touchpointId.length !== 0) {
		scope.setTag('touchpointId', extrasAndTags.touchpointId);
	}
};

/**
 * Sets the Atlassian account ID on the global Sentry scope so all
 * subsequent events include the user identity.
 *
 * Call this once the user has authenticated (e.g. in UserProvider).
 */
export const setSentryUser = (accountId: string): void => {
	Sentry.setUser({ id: accountId });
};

/**
 * Captures an exception with optional structured metadata (extras & tags).
 *
 * The `extrasAndTags` object is attached both as Sentry extras (for search)
 * and selectively promoted to tags (for filtering/alerting).
 */
export const logException = (exception: Error | unknown, extrasAndTags?: ExtrasAndTags): void => {
	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureException(exception);
	});
};

/**
 * Captures an informational message with optional structured metadata.
 */
export const logMessage = (message: string, extrasAndTags?: ExtrasAndTags): void => {
	Sentry.withScope((scope) => {
		scope.setTag('name', DEFAULT_PRODUCT);

		if (extrasAndTags) {
			scope.setExtras(extrasAndTags);
			setTagsFromExtras(scope, extrasAndTags);
		}
		Sentry.captureMessage(message);
	});
};
